/**
 * The offer, changed from a room's Page and offer tab. A room made on its board keeps its offer as the board's
 * offer card, and the board's save writes the room's offer from it, so the change goes through the board's own
 * save (and its version check), never around it; a room without a board keeps its offer on the room.
 */
import type { BoardCard, BoardState, OfferJson } from "../../../shared/types";
import { ApiError, api } from "../../lib/api";

export interface OfferChange {
  label: string;
  /** Where the button goes; only a booking or buying offer has one. */
  url: string;
  /** When it appears, in minutes into the talk. */
  minute: number;
}

type OfferCard = Extract<BoardCard, { kind: "offer" }>;

/** The offer card a board carries, or null when it has none. */
export function offerCard(state: BoardState): OfferCard | null {
  return (state.room.find((c) => c.kind === "offer") as OfferCard | undefined) ?? null;
}

/** Does this offer go somewhere, so "Where it goes" can be changed? */
export function offerLinks(kind: OfferJson["kind"]): boolean {
  return kind === "call" || kind === "buy";
}

/** The board with its offer changed, everything else as it was; null when the board has no offer card. */
export function withOffer(state: BoardState, change: OfferChange): BoardState | null {
  const card = offerCard(state);
  if (!card) return null;
  const next: OfferCard = {
    ...card,
    btn: change.label.trim(),
    ...(offerLinks(card.offerType) ? { url: change.url.trim() } : {}),
    t: Math.max(0, Math.round(change.minute * 60)),
  };
  return { ...state, room: state.room.map((c) => (c.id === card.id ? next : c)) };
}

/** Why a change cannot be saved, in the host's words, or null when it can. */
export function offerProblem(kind: OfferJson["kind"], change: OfferChange): string | null {
  if (!change.label.trim()) return "Give the button a few words.";
  if (offerLinks(kind) && !/^(https?:\/\/)?\S+\.\S+$/.test(change.url.trim())) return "Enter where the button goes, like mayatorres.com/book.";
  if (!Number.isFinite(change.minute) || change.minute < 0) return "Say how many minutes in it appears.";
  return null;
}

/**
 * Save the change. Through the board when the room has one: it is read, changed and saved at the version read, and
 * read again once if another tab saved it first, so a change made elsewhere is kept.
 */
export async function saveOffer(roomId: string, hasBoard: boolean, offer: OfferJson, change: OfferChange): Promise<void> {
  if (!hasBoard) {
    await api.webinars.setOffer(roomId, {
      ...offer,
      label: change.label.trim(),
      ...(offerLinks(offer.kind) ? { url: change.url.trim() } : {}),
      appears_at: Math.max(0, Math.round(change.minute * 60)),
    });
    return;
  }
  for (let attempt = 0; attempt < 2; attempt += 1) {
    const board = await api.board.get(roomId);
    const state = withOffer(board.state, change);
    if (!state) throw new ApiError(404, "This room's offer is set on its board.");
    try {
      await api.board.save(roomId, { version: board.version, state });
      return;
    } catch (err) {
      if (!(err instanceof ApiError) || err.status !== 409 || attempt > 0) throw err;
    }
  }
}
